import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import TeamPage from "@/pages/TeamPage";
import MemberDetailPage from "@/pages/MemberDetailPage";
import TimeOffPage from "@/pages/TimeOffPage";
import SickNotesPage from "@/pages/SickNotesPage";
import PayrollPage from "@/pages/PayrollPage";
import AuditPage from "@/pages/AuditPage";
import ProfilePage from "@/pages/ProfilePage";
import SandboxPage from "@/pages/SandboxPage";
import LicensesPage from "@/pages/LicensesPage";
import ReviewsPage from "@/pages/ReviewsPage";
import CalendarPage from "@/pages/CalendarPage";
import ManualPage from "@/pages/ManualPage";
import AccessMatrixPage from "@/pages/AccessMatrixPage";
import SettingsPage from "@/pages/SettingsPage";
import HistoryPage from "@/pages/HistoryPage";
import CommissionOverviewPage from "@/pages/CommissionOverviewPage";
import CommissionImportsPage from "@/pages/CommissionImportsPage";
import CommissionBatchPage from "@/pages/CommissionBatchPage";
import CommissionMatchingPage from "@/pages/CommissionMatchingPage";
import CommissionPoliciesPage from "@/pages/CommissionPoliciesPage";
import CommissionProducersPage from "@/pages/CommissionProducersPage";
import CommissionRulesPage from "@/pages/CommissionRulesPage";
import CommissionLedgerPage from "@/pages/CommissionLedgerPage";
import CommissionPayrollPage from "@/pages/CommissionPayrollPage";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground text-sm animate-pulse">Loading Team…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.needs_profile_verification && !user.view_as && location.pathname !== "/profile") {
    return <Navigate to="/profile" replace />;
  }
  return <DashboardLayout>{children}</DashboardLayout>;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<Protected><DashboardPage /></Protected>} />
            <Route path="/team" element={<Protected><TeamPage /></Protected>} />
            <Route path="/team/:id" element={<Protected><MemberDetailPage /></Protected>} />
            <Route path="/timeoff" element={<Protected><TimeOffPage /></Protected>} />
            <Route path="/sick-notes" element={<Protected><SickNotesPage /></Protected>} />
            <Route path="/payroll" element={<Protected><PayrollPage /></Protected>} />
            <Route path="/audit" element={<Protected><AuditPage /></Protected>} />
            <Route path="/sandbox" element={<Protected><SandboxPage /></Protected>} />
            <Route path="/licenses" element={<Protected><LicensesPage /></Protected>} />
            <Route path="/reviews" element={<Protected><ReviewsPage /></Protected>} />
            <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
            <Route path="/manual" element={<Protected><ManualPage /></Protected>} />
            <Route path="/access" element={<Protected><AccessMatrixPage /></Protected>} />
            <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
            <Route path="/history" element={<Protected><HistoryPage /></Protected>} />
            <Route path="/commissions" element={<Protected><CommissionOverviewPage /></Protected>} />
            <Route path="/commissions/imports" element={<Protected><CommissionImportsPage /></Protected>} />
            <Route path="/commissions/imports/:batchId" element={<Protected><CommissionBatchPage /></Protected>} />
            <Route path="/commissions/matching" element={<Protected><CommissionMatchingPage /></Protected>} />
            <Route path="/commissions/policies" element={<Protected><CommissionPoliciesPage /></Protected>} />
            <Route path="/commissions/producers" element={<Protected><CommissionProducersPage /></Protected>} />
            <Route path="/commissions/rules" element={<Protected><CommissionRulesPage /></Protected>} />
            <Route path="/commissions/ledger" element={<Protected><CommissionLedgerPage /></Protected>} />
            <Route path="/commissions/payroll" element={<Protected><CommissionPayrollPage /></Protected>} />
            <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
